// Cart Group Discount Handler with Price Loaders
// This is the complete em-cart-discounts.js file with integrated loading functionality

// Configuration for auto-reload
const AUTO_RELOAD_CONFIG = {
  enabled: false, // Set to false to disable auto-reload
  delaySeconds: 0, // Seconds to wait after last quantity change before reloading (increased from 3)
  showCountdown: true, // Show a countdown notification before reload
};

// Add this at the top of your script
const cartUpdateQueue = {
  isUpdating: false,
  queue: [],

  async processUpdate(updateFn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ updateFn, resolve, reject });
      this.processQueue();
    });
  },

  async processQueue() {
    if (this.isUpdating || this.queue.length === 0) return;

    this.isUpdating = true;
    const { updateFn, resolve, reject } = this.queue.shift();

    try {
      const result = await updateFn();
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      this.isUpdating = false;
      // Process next item in queue
      if (this.queue.length > 0) {
        await this.processQueue();
      }
    }
  },
};

// Reload timer management
let reloadTimer = null;
let countdownInterval = null;
let countdownElement = null;

// Track previous cart subtotal threshold state (above/below €150)
let previousSubtotalAboveThreshold = null;


function customerHasWebkortingExclusion(tags) {
  if (!tags) return false;

  const excludedWebkortingTags = [
    'exclude_webkorting',
    'exclude-webkorting',
    'no_webkorting',
    'no-webkorting',
    'geen_webkorting',
    'geen-webkorting'
  ];

  const normalizedTags = tags.split(',').map(tag => tag.trim().toLowerCase()).filter(Boolean);
  return normalizedTags.some(tag => excludedWebkortingTags.includes(tag));
}
function increaseQuantity(event, id) {
  event.preventDefault();
  console.log('increaseQuantity called');
  document.querySelector(`quantity-input[id="Quantity-${id}"] .quantity__button[name="plus"]`).click();
}

function decreaseQuantity(event, id) {
  event.preventDefault();
  console.log('decreaseQuantity called');

  // Get the current quantity
  const quantityInput = document.querySelector(`quantity-input[id="Quantity-${id}"] input[name="updates[]"]`);
  const currentQuantity = parseInt(quantityInput?.value || 1);

  // If current quantity is 1, clicking decrease will make it 0
  if (currentQuantity === 1) {
    // The change event will handle the removal
    console.log('Quantity will be 0, item will be removed');
  }

  document.querySelector(`quantity-input[id="Quantity-${id}"] .quantity__button[name="minus"]`).click();
}

async function handleZeroQuantity(itemKey, lineIndex) {
  return cartUpdateQueue.processUpdate(async () => {
    console.log(`Removing item ${itemKey} from cart (quantity set to 0)`);

    try {
      // Cancel any pending reload
      if (window.cancelReload) {
        window.cancelReload();
      }

      // Show removing state
      const itemElement = document.querySelector(`[data-item-key="${itemKey}"]`);
      if (itemElement) {
        itemElement.style.opacity = '0.5';
        itemElement.style.pointerEvents = 'none';

        const priceElements = itemElement.querySelectorAll('[data-new-price]');
        priceElements.forEach((element) => {
          const priceValue = element.querySelector('.price-value');
          if (priceValue) {
            priceValue.innerHTML = '<span class="text-gray-500">Verwijderen...</span>';
          }
        });
      }

      // Remove from cart
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove item from cart');
      }

      const result = await response.json();
      console.log('Item removed successfully:', result);

      // Wait a bit to ensure Shopify has processed the change
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Verify the item was actually removed
      const verifyCart = await fetch('/cart.js').then((r) => r.json());
      const itemStillExists = verifyCart.items.some((item) => item.key === itemKey);

      if (itemStillExists) {
        console.error('Item still exists in cart after removal, retrying...');
        // Retry the removal
        const retryResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: 0,
          }),
        });

        if (!retryResponse.ok) {
          throw new Error('Failed to remove item on retry');
        }

        // Wait again
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Remove from pricing data
      delete cartPricingData[itemKey];

      // Remove from DOM with animation
      if (itemElement) {
        itemElement.style.transition = 'all 0.3s ease-out';
        itemElement.style.transform = 'translateX(100%)';
        itemElement.style.opacity = '0';

        await new Promise((resolve) => setTimeout(resolve, 300));
        itemElement.remove();

        // Check if cart is empty
        const remainingItems = document.querySelectorAll('.cart-item');
        if (remainingItems.length === 0) {
          // Wait a bit more before reload to ensure server state is updated
          await new Promise((resolve) => setTimeout(resolve, 1000));
          window.location.reload();
        } else {
          // Wait a bit more to ensure cart state is fully updated, then check threshold
          await new Promise((resolve) => setTimeout(resolve, 500));
          // Update totals - this will check for threshold crossing
          await updateCartTotals(undefined, false);
        }
      }
    } catch (error) {
      console.error('Error removing item from cart:', error);

      // Restore the item element on error
      const itemElement = document.querySelector(`[data-item-key="${itemKey}"]`);
      if (itemElement) {
        itemElement.style.opacity = '1';
        itemElement.style.pointerEvents = 'auto';
        updateVisualPrices(itemElement, itemKey, 1);
      }

      // Show error to user
      alert('Er is een fout opgetreden bij het verwijderen van het artikel. Probeer het opnieuw.');
    }
  });
}

window.removeCartItem = async function (event, itemKey, lineIndex) {
  event.preventDefault();
  event.stopPropagation();

  console.log(`Remove button clicked for item ${itemKey}, line ${lineIndex}`);

  // Disable ALL remove buttons to prevent multiple simultaneous removals
  document.querySelectorAll('.cart-remove-button').forEach((btn) => {
    btn.disabled = true;
  });

  try {
    await handleZeroQuantity(itemKey, lineIndex);

    processCartItemsPricing();
  } finally {
    // Re-enable remaining buttons
    document.querySelectorAll('.cart-remove-button').forEach((btn) => {
      btn.disabled = false;
    });
  }
};

// Store pricing data for each product in the cart
const cartPricingData = {};

// Price loader functions
function showPriceLoaders() {
  // Show loaders for cart items
  document.querySelectorAll('.cart-item').forEach((item) => {
    item.setAttribute('data-price-loading', 'true');

    // Add loader to price elements if not already present
    const priceElements = item.querySelectorAll('[data-new-price]');
    priceElements.forEach((priceElement) => {
      if (!priceElement.querySelector('.price-loader')) {
        const loader = document.createElement('span');
        loader.className = 'price-loader';
        const priceValue = priceElement.querySelector('.price-value');
        if (!priceValue) {
          // Create price value wrapper if it doesn't exist
          const valueWrapper = document.createElement('span');
          valueWrapper.className = 'price-value';
          valueWrapper.innerHTML = priceElement.innerHTML;
          priceElement.innerHTML = '';
          priceElement.appendChild(loader);
          priceElement.appendChild(valueWrapper);
        } else {
          priceElement.insertBefore(loader, priceValue);
        }
      }
    });
  });

  // Show loaders for cart totals
  const totalsContainer = document.querySelector('.js-contents-footer');
  if (totalsContainer) {
    totalsContainer.classList.add('cart-totals-loading');

    // Add loaders to total elements if not present
    ['cart-price_subtotal', 'cart-price_tax', 'cart-price_total'].forEach((id) => {
      const element = document.getElementById(id);
      if (element && !element.querySelector('.price-loader')) {
        const loader = document.createElement('span');
        loader.className = id === 'cart-price_total' ? 'price-loader large' : 'price-loader';
        const priceValue = element.querySelector('.price-value');
        if (!priceValue) {
          // Create price value wrapper if it doesn't exist
          const valueWrapper = document.createElement('span');
          valueWrapper.className = 'price-value';
          valueWrapper.innerHTML = element.innerHTML;
          element.innerHTML = '';
          element.appendChild(loader);
          element.appendChild(valueWrapper);
        } else {
          element.insertBefore(loader, priceValue);
        }
      }
    });
  }
}

// Function to hide loader for a specific item
function hidePriceLoader(itemKey) {
  const itemElement = document.querySelector(`[data-item-key="${itemKey}"]`);
  if (itemElement) {
    itemElement.setAttribute('data-price-loading', 'false');

    // Hide loaders and show prices
    const priceElements = itemElement.querySelectorAll('[data-new-price]');
    priceElements.forEach((priceElement) => {
      const loader = priceElement.querySelector('.price-loader');
      if (loader) {
        loader.style.display = 'none';
      }
      const priceValue = priceElement.querySelector('.price-value');
      if (priceValue) {
        priceValue.style.display = '';
      }
    });
  }
}

// Function to hide all cart total loaders
function hideCartTotalLoaders() {
  const totalsContainer = document.querySelector('.js-contents-footer');
  if (totalsContainer) {
    totalsContainer.classList.remove('cart-totals-loading');

    // Hide loaders and show values
    totalsContainer.querySelectorAll('.price-loader').forEach((loader) => {
      loader.style.display = 'none';
    });
    totalsContainer.querySelectorAll('.price-value').forEach((value) => {
      value.style.display = '';
    });
  }
}

// Initialize the group discount system
async function initializeGroupDiscountSystem() {
  console.log('Initializing group discount system with loaders...');

  // Show loaders immediately
  showPriceLoaders();

  // Get current cart
  const cart = await fetch('/cart.js').then((r) => r.json());

  // Fetch pricing data for each item FIRST
  for (const item of cart.items) {
    await fetchPricingDataForItem(item);
  }

  // Initialize threshold tracking AFTER pricing data is loaded
  let initialSubtotal = 0;
  cart.items.forEach((item) => {
    const priceInfo = calculatePriceForQuantity(item.key, item.quantity);
    if (priceInfo) {
      initialSubtotal += priceInfo.totalPrice;
    } else {
      initialSubtotal += item.final_line_price / 100;
    }
  });
  previousSubtotalAboveThreshold = initialSubtotal >= 150;
  console.log(
    `Initial cart subtotal: €${initialSubtotal.toFixed(2)}, above threshold: ${previousSubtotalAboveThreshold}`
  );

  // Check and update properties for existing items
  await checkAndUpdateAllCartItems(cart);

  // Listen for quantity changes
  setupQuantityChangeListeners();
}

// Fetch pricing data for a cart item
// Updated fetchPricingDataForItem function to match Script 1's approach
async function fetchPricingDataForItem(item) {
  const sku = item.sku;

  // Get customer info from data attributes
  const customerDataElement = document.getElementById('customer-data');
  let customerTags = '';
  let customerEmail = '';

  if (customerDataElement) {
    customerTags = customerDataElement.getAttribute('data-customer-tags') || '';
    customerEmail = customerDataElement.getAttribute('data-customer-email') || '';
  }

  // Extract email from tags if present
  const emailFromTags = extractEmailFromTags(customerTags) || customerEmail.trim(); // terugval: net geregistreerd account heeft nog geen e-mailtag
  let customerId = '';

  if (emailFromTags) {
    console.log('Found email in tags:', emailFromTags);
    // Look up customer by email
    const customerData = await lookupCustomerByEmail(emailFromTags);
    if (customerData && customerData.number) {
      customerId = customerData.number;
      customerEmail = emailFromTags;
      console.log('Found customer ID from email lookup:', customerId);
    }
  } else if (customerEmail) {
    // Use the customer's actual email if no email in tags
    const customerData = await lookupCustomerByEmail(customerEmail);
    if (customerData && customerData.number) {
      customerId = customerData.number;
      console.log('Found customer ID from customer email:', customerId);
    }
  }

  // Fallback to default if no customer ID found
  if (!customerId) {
    customerId = 'CN.0003418'; // Default anonymous customer
    console.log('Using default customer ID:', customerId);
  }

  console.log(`Fetching pricing data for SKU: ${sku}, Customer: ${customerId}`);

  try {
    // First get item info
    const itemInfoResponse = await fetch(`https://kiwitz-backend-jttm8.ondigitalocean.app/api/item-info?itemNo=${sku}`);
    const itemInfo = await itemInfoResponse.json();
    const itemDiscountGroup = itemInfo.value?.[0]?.ItemDiscountGroup || null;

    // Get ALL customer discount groups from tags (not just the first one)
    const customerDiscountGroups = getCustomerDiscountGroups(customerTags);

    // Fetch product rules - UPDATED to match Script 1
    const response = await fetch('https://kiwitz-backend-jttm8.ondigitalocean.app/api/product-rules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        itemNo: sku,
        customerId: customerId,
        articleGroupCode: itemDiscountGroup,
        customerGroupCodes: customerDiscountGroups, // Changed to plural and sending array
        isAnonymous: !customerId || customerId === 'CN.0003418',
      }),
    });

    const data = await response.json();
    console.log(`Product rules response for ${sku}:`, data);

    if (data.pricing && data.pricing.basePrices) {
      // Apply the same merging logic as the cart script
      let mergedBasePrices;
      if (!customerId || customerId === 'CN.0003418') {
        mergedBasePrices = data.pricing.basePrices;
      } else {
        // Extract customer group prices if available
        let customerGroupPrices = data.customerGroupPrices || [];

        // Merge all pricing sources
        mergedBasePrices = mergePrices(
          data.allCustomerPrices,
          data.pricing.basePrices,
          customerGroupPrices,
          data.pricing.discountRules
        );
      }

      // Filter out higher prices
      const filteredMergedPrices = filterOutHigherPrices(mergedBasePrices);

      // Store the MERGED AND FILTERED pricing data
      cartPricingData[item.key] = {
        basePrices: filteredMergedPrices, // Use filtered merged prices!
        discountRules: data.pricing.discountRules || [],
        customerGroupCodes: customerDiscountGroups,
        customerId: customerId,
        allowLineDiscMap: createAllowLineDiscMap(filteredMergedPrices),
      };

      console.log(`Stored pricing data for ${sku}:`, cartPricingData[item.key]);
      hideCartTotalLoaders();
    }
  } catch (error) {
    console.error(`Error fetching pricing data for ${sku}:`, error);
    // Hide loader on error
    hidePriceLoader(item.key);
  }
}

// Extract email from customer tags
function extractEmailFromTags(tags) {
  if (!tags) return null;

  const tagsArray = tags.split(',').map((tag) => tag.trim());

  // Find email address in tags
  const emailTag = tagsArray.find((tag) => {
    // Basic email validation
    return tag.includes('@') && tag.includes('.') && !tag.includes(' ');
  });

  return emailTag || null;
}

// Look up customer by email using the backend API
async function lookupCustomerByEmail(email) {
  if (!email) return null;

  try {
    const response = await fetch('https://kiwitz-backend-jttm8.ondigitalocean.app/api/customer-by-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    });

    if (!response.ok) {
      console.error('Customer lookup failed:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('Customer lookup result:', data);
    return data;
  } catch (error) {
    console.error('Error looking up customer by email:', error);
    return null;
  }
}

// Create a map of quantities to allowLineDisc values
function createAllowLineDiscMap(basePrices) {
  const map = {};
  basePrices.forEach((price) => {
    map[price.minQuantity] = price.allowLineDisc !== false;
  });
  return map;
}

// Get customer discount group from tags (same logic as main product page)
function getCustomerDiscountGroups(tags) {
  if (!tags) return [];

  // Convert the comma-separated string to an array
  const tagsArray = tags.split(',').map((tag) => tag.trim());

  // Define tags to exclude
  const excludeTags = ['Logged In', 'Is In Company'];

  // Filter out excluded tags and email addresses
  const discountGroups = tagsArray.filter((tag) => {
    // Skip if it's in the exclude list
    if (excludeTags.includes(tag)) return false;
    // Skip if it looks like an email
    if (tag.includes('@')) return false;
    // Skip if it's empty
    if (!tag) return false;

    return true;
  });

  // Return ALL valid discount group tags
  console.log('Found discount groups from tags:', discountGroups);
  return discountGroups;
}

// Check if group discount should be allowed for a quantity
function shouldAllowGroupDiscount(itemKey, quantity) {
  const pricingData = cartPricingData[itemKey];
  if (!pricingData || !pricingData.basePrices) return false;

  // Find the applicable price tier for this quantity
  let applicablePrice = pricingData.basePrices[0];
  for (let i = pricingData.basePrices.length - 1; i >= 0; i--) {
    if (quantity >= pricingData.basePrices[i].minQuantity) {
      applicablePrice = pricingData.basePrices[i];
      break;
    }
  }

  // Return the allowLineDisc value from the applicable tier
  return applicablePrice ? applicablePrice.allowLineDisc !== false : false;
}

function calculatePriceForQuantity(itemKey, quantity) {
  const pricingData = cartPricingData[itemKey];
  console.log('cartPricingData', cartPricingData);
  if (!pricingData || !pricingData.basePrices || pricingData.basePrices.length === 0) {
    console.log('No basePrices data');
    return null;
  }

  console.log('pricingData', pricingData);

  // Find the applicable price tier for this quantity
  let applicablePrice = pricingData.basePrices[0];
  for (let i = pricingData.basePrices.length - 1; i >= 0; i--) {
    if (quantity >= pricingData.basePrices[i].minQuantity) {
      applicablePrice = pricingData.basePrices[i];
      break;
    }
  }

  if (!applicablePrice) {
    console.log('No applicable price tier found');
    return null;
  }

  // IMPORTANT: Check if we have an effectivePrice from the merge function
  let unitPrice = applicablePrice.effectivePrice || applicablePrice.unitPrice;

  // Get the discount percent from the merged data
  const tierDiscountPercent = applicablePrice.discountPercent || 0;
  const allowsDiscount = applicablePrice.allowLineDisc !== false;

  // Check if discount applies
  const hasDiscount =
    allowsDiscount && tierDiscountPercent > 0 && pricingData.customerId && pricingData.customerId !== 'CN.0003418';

  return {
    unitPrice: unitPrice, // This should now be 121.50 instead of 150
    totalPrice: unitPrice * quantity,
    originalUnitPrice: applicablePrice.unitPrice,
    originalTotalPrice: applicablePrice.unitPrice * quantity,
    hasDiscount: hasDiscount || unitPrice < applicablePrice.unitPrice, // Also check if price is different
    discountPercent: tierDiscountPercent,
    tierDiscount: tierDiscountPercent,
  };
}

// Update visual prices for a cart item
// Updated updateVisualPrices function to properly display all customer groups
function updateVisualPrices(itemElement, itemKey, quantity) {
  const priceInfo = calculatePriceForQuantity(itemKey, quantity);
  console.log('priceInfo', priceInfo);

  // If no price info available, just hide loader and let default prices show
  if (!priceInfo) {
    console.log('no price info');
    hidePriceLoader(itemKey);
    hideCartTotalLoaders();
    return;
  }

  // Update the displayed prices
  const newPriceElements = itemElement.querySelectorAll('[data-new-price]');
  newPriceElements.forEach((element) => {
    const formattedPrice = `€${priceInfo.totalPrice.toFixed(2).replace('.', ',')}`;

    // Find or create price value element
    let priceValue = element.querySelector('.price-value');
    if (!priceValue) {
      priceValue = document.createElement('span');
      priceValue.className = 'price-value';
      element.appendChild(priceValue);
    }

    if (priceInfo.hasDiscount) {
      // Show discounted price with tier-specific discount percentage
      priceValue.innerHTML = `
        ${formattedPrice}
        <span class="text-accent ml-1">(-${Math.round(priceInfo.discountPercent)}%)</span>
        <span class="text-xs line-through text-gray-500 ml-2">€${priceInfo.originalTotalPrice
          .toFixed(2)
          .replace('.', ',')}</span>
        <span class="text-[#666666] text-xs ml-1">(excl. btw)</span>
      `;
    } else {
      priceValue.innerHTML = `${formattedPrice} <span class="text-[#666666] text-xs ml-1">(excl. btw)</span>`;
    }
  });

  // Store calculated unit price for Bold rules
  itemElement.setAttribute('data-calculated-unit-price', priceInfo.unitPrice.toFixed(2));
  itemElement.setAttribute('data-tier-discount', priceInfo.discountPercent);

  // Update or show group discount tag with ALL customer groups (like Script 1)
  const pricingData = cartPricingData[itemKey];
  if (pricingData && pricingData.customerGroupCodes) {
    const groupDiscountTag = itemElement.querySelector('.customer-group-code');
    if (groupDiscountTag) {
      if (priceInfo.hasDiscount && pricingData.customerGroupCodes.length > 0) {
        groupDiscountTag.classList.remove('hidden');
        // Show ALL applicable groups (matching Script 1)
        const groupsDisplay = pricingData.customerGroupCodes.join(', ');
        groupDiscountTag.innerHTML = `<span class="pr-1">Klantenkortingsgroep</span> ${groupsDisplay} (${Math.round(
          priceInfo.discountPercent
        )}%)`;
      } else {
        groupDiscountTag.classList.add('hidden');
      }
    }
  }

  // Hide loader for this item
  hidePriceLoader(itemKey);

  // Update cart totals (skip threshold check here - it will be checked after cart update)
  updateCartTotals(undefined, true);
}

// Check if cart subtotal has crossed the €150 threshold and reload if needed
function checkAndReloadIfThresholdCrossed(currentSubtotal, skipStateUpdate = false) {
  const WEBKORTING_MIN_ORDER_VALUE = 150;
  const currentAboveThreshold = currentSubtotal >= WEBKORTING_MIN_ORDER_VALUE;

  // Only check if we have a previous state (not on initial load)
  if (previousSubtotalAboveThreshold !== null) {
    const hasCrossedThreshold = previousSubtotalAboveThreshold !== currentAboveThreshold;

    if (hasCrossedThreshold) {
      console.log(
        `Cart subtotal crossed €${WEBKORTING_MIN_ORDER_VALUE} threshold: ${
          previousSubtotalAboveThreshold ? 'above' : 'below'
        } → ${currentAboveThreshold ? 'above' : 'below'}. Reloading page...`
      );
      // Small delay to ensure cart state is updated on server
      setTimeout(() => {
        window.location.reload();
      }, 500);
      return true; // Indicate that reload was triggered
    }
  }

  // Update the previous state (unless we're skipping for initial calculations)
  if (!skipStateUpdate) {
    previousSubtotalAboveThreshold = currentAboveThreshold;
  }
  return false; // No reload needed
}

async function updateCartTotals(providedSubtotal, skipThresholdCheck = false) {
  console.log(
    'updateCartTotals called with providedSubtotal:',
    providedSubtotal,
    'skipThresholdCheck:',
    skipThresholdCheck
  );
  const WEBKORTING_MIN_ORDER_VALUE = 150;
  const WEBKORTING_PERCENTAGE = 0.02; // 2%
  const VAT_RATE = 0.21; // 21%

  let subtotal = providedSubtotal;

  // If subtotal not provided, calculate it from cart items
  if (!subtotal || subtotal === undefined) {
    console.log('No subtotal provided, calculating from cart items...');

    // Only wait if we're checking threshold (to ensure cart is updated)
    if (!skipThresholdCheck) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    const cart = await fetch('/cart.js').then((r) => r.json());
    subtotal = 0;

    // Calculate subtotal from all items using their pricing data
    cart.items.forEach((item) => {
      const priceInfo = calculatePriceForQuantity(item.key, item.quantity);

      if (priceInfo) {
        // Use the calculated price (already excluding VAT)
        subtotal += priceInfo.totalPrice;
        console.log(`Item ${item.sku}: €${priceInfo.totalPrice.toFixed(2)} (qty: ${item.quantity})`);
      } else {
        // Fallback: use Shopify's price (convert from cents and assume it's excluding VAT)
        const fallbackPrice = item.final_line_price / 100;
        subtotal += fallbackPrice;
        console.log(`Item ${item.sku}: Using fallback price €${fallbackPrice.toFixed(2)}`);
      }
    });

    console.log('Calculated subtotal:', subtotal, 'Previous threshold state:', previousSubtotalAboveThreshold);
  }

  // Check if threshold was crossed (only if not skipping and we have a previous state)
  if (!skipThresholdCheck && previousSubtotalAboveThreshold !== null) {
    const reloadTriggered = checkAndReloadIfThresholdCrossed(subtotal);
    if (reloadTriggered) {
      // If reload is triggered, return early - page will reload
      return {
        subtotal,
        webkortingAmount: 0,
        subtotalAfterDiscount: subtotal,
        vatAmount: 0,
        total: subtotal,
      };
    }
  }

  const customerDataElement = document.getElementById('customer-data');
  const customerTags = customerDataElement?.getAttribute('data-customer-tags') || '';
  const excludeFromWebkorting = customerHasWebkortingExclusion(customerTags);

  // Update subtotal display
  const subtotalElement = document.getElementById('cart-price_subtotal');
  if (subtotalElement) {
    const subtotalValue = subtotalElement.querySelector('.price-value');
    if (subtotalValue) {
      subtotalValue.innerHTML = `€${subtotal.toFixed(2).replace('.', ',')}`;
    }
  }

  // Calculate webkorting if applicable
  let webkortingAmount = 0;
  let subtotalAfterDiscount = subtotal;

  const webDiscountElement = document.querySelector('[data-web-discount]');
  const webDiscountAmountElement = document.getElementById('cart-price_web_discount');

  const excludeWebkorting = customerDataElement?.getAttribute('data-exclude-webkorting') === 'true';

  const ownCodeApplied = window.__webkortingUserOverride === true || window.__foreignCodeApplied === true || (window.__appliedDiscountCode && window.__appliedDiscountCode.indexOf('webkorting') === -1);

  if (!excludeFromWebkorting && !excludeWebkorting && !ownCodeApplied && subtotal >= WEBKORTING_MIN_ORDER_VALUE) {
    // Calculate 2% discount
    webkortingAmount = subtotal * WEBKORTING_PERCENTAGE;
    subtotalAfterDiscount = subtotal - webkortingAmount;

    // Show webkorting line
    if (webDiscountElement) {
      webDiscountElement.style.display = '';

      if (webDiscountAmountElement) {
        const discountValue = webDiscountAmountElement.querySelector('.price-value');
        if (discountValue) {
          discountValue.innerHTML = `- €${webkortingAmount.toFixed(2).replace('.', ',')}`;
        }
      }
    }
  } else {
    // Hide webkorting line when order does not qualify or the customer is excluded
    if (webDiscountElement) {
      webDiscountElement.style.display = 'none';
    }

    console.log(excludeFromWebkorting ? 'Not applying webkorting because customer is excluded' : 'Not applying webkorting');
    // Don't mutate Bold's discount state from the display path; only
    // hideAndApplyWebkorting (eligibility) and explicit user actions do that.
  }

  // Calculate VAT on the exact discounted subtotal
  const vatAmount = subtotalAfterDiscount * VAT_RATE;

  // Calculate total using full precision values
  const total = subtotalAfterDiscount + vatAmount;

  // Round at the end for display
  const roundedTotal = Math.round(total * 100) / 100;
  const roundedVAT = Math.round(vatAmount * 100) / 100;

  // Update VAT display
  const vatElement = document.getElementById('cart-price_tax');
  if (vatElement) {
    const vatValue = vatElement.querySelector('.price-value');
    if (vatValue) {
      vatValue.innerHTML = `€${roundedVAT.toFixed(2).replace('.', ',')}`;
    }
  }

  // Update total
  const totalPriceElement = document.getElementById('cart-price_total');
  if (totalPriceElement) {
    const totalValue = totalPriceElement.querySelector('.price-value');
    if (totalValue) {
      totalValue.innerHTML = `€${roundedTotal.toFixed(2).replace('.', ',')}`;
    }
  }

  // Hide cart total loaders
  hideCartTotalLoaders();

  // Log the calculation for debugging
  console.log('Cart totals calculation:', {
    subtotal: subtotal,
    excludeFromWebkorting: excludeFromWebkorting,
    webkortingAmount: webkortingAmount,
    webkortingAmountRounded: Math.round(webkortingAmount * 100) / 100,
    subtotalAfterDiscount: subtotalAfterDiscount,
    vatAmount: vatAmount,
    vatAmountRounded: roundedVAT,
    totalBeforeRounding: total,
    totalAfterRounding: roundedTotal,
  });

  return {
    subtotal,
    webkortingAmount,
    subtotalAfterDiscount,
    vatAmount: roundedVAT,
    total: roundedTotal,
  };
}

// Updated updateCartTotals function with 2% web discount
// async function updateCartTotals2() {
//   console.log('updateCartTotals called');
//   const cart = await fetch('/cart.js').then(r => r.json());

//   let subtotalExclVat = 0;
//   let totalLineDiscounts = 0;
//   const vatRate = 0.21;
//   const webDiscountRate = 0.02; // 2% web discount

//   cart.items.forEach(item => {
//     const priceInfo = calculatePriceForQuantity(item.key, item.quantity);
//     console.log(`Price info for ${item.sku} (qty: ${item.quantity}):`, priceInfo);

//     if (priceInfo) {
//       // Price is already excluding VAT
//       subtotalExclVat += priceInfo.totalPrice;

//       if (priceInfo.hasDiscount) {
//         // Calculate line discount (prices already exclude VAT)
//         const discountAmount = priceInfo.originalTotalPrice - priceInfo.totalPrice;
//         totalLineDiscounts += discountAmount;
//         console.log(`Line discount for ${item.sku}: €${discountAmount.toFixed(2)} (${priceInfo.discountPercent}%)`);
//       }
//     } else {
//       // fallback: price is already excluding VAT
//       subtotalExclVat += item.final_line_price / 100;
//       console.log(`No pricing data for ${item.sku}, using cart price`);
//     }
//   });

//   // Calculate 2% web discount on subtotal (after line discounts)
//   const webDiscountAmount = subtotalExclVat * webDiscountRate;
//   const subtotalAfterWebDiscount = subtotalExclVat - webDiscountAmount;

//   // Calculate VAT on the subtotal AFTER web discount
//   const vatAmount = subtotalAfterWebDiscount * vatRate;

//   // Calculate total including VAT
//   const totalInclVat = subtotalAfterWebDiscount + vatAmount;

//   // Log the calculated values
//   console.log('Subtotaal excl. BTW: €' + subtotalExclVat.toFixed(2));
//   console.log('Line kortingen: €' + totalLineDiscounts.toFixed(2));
//   console.log('2% Web korting: €' + webDiscountAmount.toFixed(2));
//   console.log('BTW (21%): €' + vatAmount.toFixed(2));
//   console.log('Totaal incl. BTW: €' + totalInclVat.toFixed(2));

//   // Update "Totaal artikelen" (subtotal excluding VAT)
//   const subtotalElement = document.getElementById('cart-price_subtotal');
//   if (subtotalElement) {
//     let priceValue = subtotalElement.querySelector('.price-value');
//     if (!priceValue) {
//       priceValue = document.createElement('span');
//       priceValue.className = 'price-value';
//       // subtotalElement.appendChild(priceValue);
//     }
//     // priceValue.textContent = `€${subtotalExclVat.toFixed(2).replace('.', ',')}`;
//   }

//   // Update 2% web discount
//   const webDiscountElement = document.getElementById('cart-price_web_discount');
//   if (webDiscountElement) {
//     let priceValue = webDiscountElement.querySelector('.price-value');
//     if (!priceValue) {
//       priceValue = document.createElement('span');
//       priceValue.className = 'price-value';
//       webDiscountElement.appendChild(priceValue);
//     }
//     priceValue.textContent = `-€${webDiscountAmount.toFixed(2).replace('.', ',')}`;
//   }

//   // Update BTW (calculated on subtotal after web discount)
//   const vatElement = document.getElementById('cart-price_tax');
//   if (vatElement) {
//     let priceValue = vatElement.querySelector('.price-value');
//     if (!priceValue) {
//       priceValue = document.createElement('span');
//       priceValue.className = 'price-value';
//       vatElement.appendChild(priceValue);
//     }
//     priceValue.textContent = `€${vatAmount.toFixed(2).replace('.', ',')}`;
//   }

//   // Update "Korting" display (only line discounts - not web discount)
//   const discountElement = document.getElementById('cart-price_discount');
//   const discountContainers = document.querySelectorAll('[data-cart-discount]');

//   if (totalLineDiscounts > 0) {
//     if (discountElement) {
//       discountElement.textContent = `-€${totalLineDiscounts.toFixed(2).replace('.', ',')}`;
//     }
//     discountContainers.forEach(container => {
//       container.classList.add('flex');
//       container.classList.remove('hidden');
//     });
//   } else {
//     discountContainers.forEach(container => {
//       container.classList.remove('flex');
//       container.classList.add('hidden');
//     });
//   }

//   // Update total
//   const totalElement = document.getElementById('cart-price_total');
//   if (totalElement) {
//     let priceValue = totalElement.querySelector('.price-value');
//     if (!priceValue) {
//       priceValue = document.createElement('span');
//       priceValue.className = 'price-value';
//       totalElement.appendChild(priceValue);
//     }
//     priceValue.textContent = `€${totalInclVat.toFixed(2).replace('.', ',')}`;
//   }

//   // Hide cart total loaders
//   hideCartTotalLoaders();
// }

// Update cart item properties in the background
async function updateCartItemPropertiesBackground(itemKey, newQuantity, lineIndex) {
  const shouldAllow = shouldAllowGroupDiscount(itemKey, newQuantity);
  const pricingData = cartPricingData[itemKey];

  console.log(
    `Updating properties in background for item ${itemKey}, quantity: ${newQuantity}, allow discount: ${shouldAllow}`
  );

  try {
    // Get current cart to get current properties
    const cart = await fetch('/cart.js').then((r) => r.json());
    const item = cart.items.find((i) => i.key === itemKey);

    if (!item) {
      console.error('Item not found in cart');
      return;
    }

    // Check if we actually need to update
    const hasGroupDiscountProperty = item.properties && item.properties['_allow_group_discount'] === 'true';
    // For testing: Remove the discount percent check
    // const shouldHaveProperty = shouldAllow && pricingData?.discountPercent > 0;
    const shouldHaveProperty = shouldAllow; // TEST MODE - adds property even with 0% discount

    console.log('Current has property:', hasGroupDiscountProperty);
    console.log('Should have property:', shouldHaveProperty);

    // Only update if there's a change needed
    if (hasGroupDiscountProperty === shouldHaveProperty && item.quantity === newQuantity) {
      console.log('No property update needed');
      return;
    }

    // Build properties object
    let properties = {};

    // Copy existing properties except _allow_group_discount
    if (item.properties) {
      Object.keys(item.properties).forEach((key) => {
        if (key !== '_allow_group_discount') {
          properties[key] = item.properties[key];
        }
      });
    }

    // Add or remove the group discount property
    // if (shouldHaveProperty) {
    //   properties['_allow_group_discount'] = 'true';
    //   console.log('Adding _allow_group_discount property');
    // } else {
    //   properties['_allow_group_discount'] = 'false';
    //   console.log('Removing _allow_group_discount property');
    // }

    // Prepare the update data.
    // Properties alleen meesturen als ze echt wijzigen: Shopify geeft de cartregel bij elke
    // change.js mét properties een nieuwe line key, waardoor cartPricingData en de
    // data-item-key's in de DOM verouderen (sidebar valt terug op brutoprijs, tweede
    // aantal-wijziging wordt genegeerd).
    const currentProperties = item.properties || {};
    const propertiesChanged =
      Object.keys(properties).length !== Object.keys(currentProperties).length ||
      Object.keys(properties).some((key) => properties[key] !== currentProperties[key]);

    const updateData = {
      line: lineIndex,
      quantity: newQuantity,
    };
    if (propertiesChanged) {
      updateData.properties = properties;
    }

    console.log('Sending background update:', JSON.stringify(updateData, null, 2));

    // Update the cart in the background
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update cart: ${errorText}`);
    }

    const result = await response.json();
    console.log('Background update successful:', result);

    // Vangnet: is de line key toch veranderd, verhuis dan de prijsdata en de DOM-referenties.
    const updatedItem = result && result.items ? result.items[lineIndex - 1] : null;
    if (updatedItem && updatedItem.key && updatedItem.key !== itemKey) {
      rekeyCartItem(itemKey, updatedItem.key);
    }
  } catch (error) {
    console.error('Error updating cart item properties:', error);
  }
}

// Verhuis prijsdata en DOM-referenties van een oude naar een nieuwe Shopify line key
function rekeyCartItem(oldKey, newKey) {
  if (!oldKey || !newKey || oldKey === newKey) return;
  console.log(`Cart line key changed: ${oldKey} -> ${newKey}, rekeying pricing data`);
  if (cartPricingData[oldKey]) {
    cartPricingData[newKey] = cartPricingData[oldKey];
    delete cartPricingData[oldKey];
  }
  document.querySelectorAll(`[data-item-key="${oldKey}"]`).forEach((element) => {
    element.setAttribute('data-item-key', newKey);
  });
}

// Check and update all cart items on page load
async function checkAndUpdateAllCartItems(cart) {
  console.log('Checking all cart items for property updates...');

  let itemsWithoutPricingData = 0;

  for (let i = 0; i < cart.items.length; i++) {
    const item = cart.items[i];
    const lineIndex = i + 1; // Line index is 1-based
    const pricingData = cartPricingData[item.key];

    if (!pricingData) {
      console.log(`No pricing data for item ${item.key}, skipping...`);
      // Hide loader for this item even if no pricing data
      hidePriceLoader(item.key);
      itemsWithoutPricingData++;
      continue;
    }

    // Update visual prices
    const itemElement = document.querySelector(`[data-item-key="${item.key}"]`);
    if (itemElement) {
      updateVisualPrices(itemElement, item.key, item.quantity);
    }

    // Check if properties need updating
    const shouldAllow = shouldAllowGroupDiscount(item.key, item.quantity);
    const hasGroupDiscountProperty = item.properties && item.properties['_allow_group_discount'] === 'true';
    const shouldHaveProperty = shouldAllow && pricingData.discountPercent > 0;

    console.log(
      `Item ${item.sku}: quantity=${item.quantity}, hasProperty=${hasGroupDiscountProperty}, shouldHaveProperty=${shouldHaveProperty}`
    );

    // If the property state doesn't match what it should be, update it immediately
    if (hasGroupDiscountProperty !== shouldHaveProperty) {
      console.log(`Property mismatch for ${item.sku}, updating immediately...`);
      // Wait for this initial update to complete
      await updateCartItemPropertiesBackground(item.key, item.quantity, lineIndex);
    }
  }

  // If all items have no pricing data, hide cart total loaders too
  if (itemsWithoutPricingData === cart.items.length) {
    hideCartTotalLoaders();
  }
}

// Setup listeners for quantity changes
function setupQuantityChangeListeners() {
  // Intercept Shopify's cart update requests
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const [url, options] = args;

    // Check if this is a cart change request
    if (url && url.includes('/cart/change.js') && options && options.body) {
      try {
        const body = JSON.parse(options.body);
        console.log('Intercepted cart change request:', body);

        // If it's a quantity change by ID without properties, handle it ourselves
        if (body.id && body.quantity !== undefined && !body.properties) {
          console.log('Intercepting to add properties...');

          // Find the item in our cart data
          const itemKey = Object.keys(cartPricingData).find((key) => {
            // You might need to match by variant ID here
            return true; // Implement proper matching
          });

          if (itemKey && cartPricingData[itemKey]) {
            // Add our properties
            const shouldAllow = shouldAllowGroupDiscount(itemKey, body.quantity);
            const shouldHaveProperty = shouldAllow && cartPricingData[itemKey].discountPercent > 0;

            body.properties = body.properties || {};
            // body.properties['_allow_group_discount'] = shouldHaveProperty ? 'true' : 'false';

            console.log('Modified request with properties:', body);
            options.body = JSON.stringify(body);
          }
        }
      } catch (e) {
        console.error('Error intercepting cart request:', e);
      }
    }

    return originalFetch.apply(this, args);
  };

  // Listen for Shopify's quantity input changes
  document.addEventListener('change', async function (e) {
    if (e.target.matches('input[name="updates[]"]')) {
      const lineIndex = parseInt(e.target.getAttribute('data-index'));
      const newQuantity = parseInt(e.target.value) || 0;
      const itemElement = e.target.closest('[data-item-key]');
      const itemKey = itemElement?.getAttribute('data-item-key');

      if (itemKey && cartPricingData[itemKey]) {
        // Check if quantity is 0
        if (newQuantity === 0) {
          await handleZeroQuantity(itemKey, lineIndex);
          return; // Exit early, no need to update prices or properties
        }

        // Update visual prices immediately (without threshold check)
        updateVisualPrices(itemElement, itemKey, newQuantity);

        // Update quantity/properties in background
        const backgroundUpdate = updateCartItemPropertiesBackground(itemKey, newQuantity, lineIndex);

        // Wait for cart to update on server, then check threshold
        setTimeout(async () => {
          // Eerst wachten tot de cart-update (incl. eventuele rekey van de line key) klaar is,
          // anders valt de sidebar kort terug op de brutoprijs
          try {
            await backgroundUpdate;
          } catch (e) {
            // fout is al gelogd in updateCartItemPropertiesBackground
          }
          // Update totals - this will check threshold after cart is updated
          await updateCartTotals(undefined, false);
        }, 800);

        // Handle reload timer
        handleReloadTimer();
      }
    }
  });

  // Also listen for button clicks to reset timer
  document.addEventListener('click', function (e) {
    if (
      e.target.matches('[data-quantity-decrease], [data-quantity-increase]') ||
      e.target.closest('[data-quantity-decrease], [data-quantity-increase]')
    ) {
      handleReloadTimer();
    }
  });

  // Listen for cart update events
  document.addEventListener('cart:change', async function (e) {
    console.log('Cart change event:', e.detail);
    // Show loaders for any changes
    showPriceLoaders();
    // Update visual prices
    const cart = await fetch('/cart.js').then((r) => r.json());
    await checkAndUpdateAllCartItems(cart);
  });
}

// Handle the reload timer
function handleReloadTimer() {
  if (!AUTO_RELOAD_CONFIG.enabled) return;

  // Clear existing timers
  if (reloadTimer) {
    clearTimeout(reloadTimer);
    reloadTimer = null;
  }

  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }

  // Remove countdown element if it exists
  if (countdownElement) {
    countdownElement.remove();
    countdownElement = null;
  }

  // Set new reload timer
  reloadTimer = setTimeout(() => {
    if (AUTO_RELOAD_CONFIG.showCountdown) {
      showReloadCountdown();
    } else {
      window.location.reload();
    }
  }, AUTO_RELOAD_CONFIG.delaySeconds * 1000);
}

// Show countdown notification before reload
async function showReloadCountdown() {
  // Wait for all pending property updates to complete
  if (window.pendingPropertyUpdates && window.pendingPropertyUpdates.size > 0) {
    console.log('Waiting for pending property updates before reload...');

    // Show a "updating" message
    countdownElement = document.createElement('div');
    countdownElement.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #FFA500;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 9999;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 10px;
    `;

    countdownElement.innerHTML = `
      <span>Properties worden bijgewerkt...</span>
      <div style="
        width: 20px;
        height: 20px;
        border: 3px solid rgba(255,255,255,0.3);
        border-radius: 50%;
        border-top-color: white;
        animation: spin 1s linear infinite;
      "></div>
      <style>
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;

    document.body.appendChild(countdownElement);

    try {
      // Wait for all updates with timeout
      await Promise.race([
        Promise.all(Array.from(window.pendingPropertyUpdates)),
        new Promise((resolve) => setTimeout(resolve, 5000)), // 5 second timeout
      ]);
      console.log('All property updates completed');

      // Add extra delay to ensure Shopify has processed the changes
      console.log('Adding extra delay for Shopify to process changes...');
      await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second extra delay
    } catch (error) {
      console.error('Error waiting for updates:', error);
    }

    // Remove the updating message
    if (countdownElement) {
      countdownElement.remove();
    }
  }

  let secondsLeft = 3; // Countdown duration

  // Create countdown element
  countdownElement = document.createElement('div');
  countdownElement.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #07B800;
    color: white;
    padding: 15px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    z-index: 9999;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 10px;
  `;

  countdownElement.innerHTML = `
    <span>Prijzen worden bijgewerkt in <span id="countdown-seconds">${secondsLeft}</span>...</span>
    <button onclick="window.cancelReload()" style="
      background: white;
      color: #07B800;
      border: none;
      padding: 5px 10px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: bold;
    ">Annuleren</button>
  `;

  document.body.appendChild(countdownElement);

  // Update countdown
  countdownInterval = setInterval(() => {
    secondsLeft--;
    const countdownSpan = document.getElementById('countdown-seconds');
    if (countdownSpan) {
      countdownSpan.textContent = secondsLeft;
    }

    if (secondsLeft <= 0) {
      clearInterval(countdownInterval);
      window.location.reload();
    }
  }, 1000);
}

// Global function to cancel reload
window.cancelReload = function () {
  if (reloadTimer) {
    clearTimeout(reloadTimer);
    reloadTimer = null;
  }

  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }

  if (countdownElement) {
    countdownElement.remove();
    countdownElement = null;
  }

  console.log('Cart reload cancelled');
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function () {
  // Show loaders immediately if cart items exist
  if (document.querySelector('.cart-item')) {
    showPriceLoaders();
  }

  initializeGroupDiscountSystem();

  // Log configuration on load
  console.log('Auto-reload configuration:', AUTO_RELOAD_CONFIG);
});

// Listen for Shopify cart updates
document.addEventListener('cart:updated', function () {
  updateCartTotals();
});

// Clean up on page unload
window.addEventListener('beforeunload', function () {
  window.cancelReload();
});

// Debug function to manually test adding the property
window.debugAddGroupDiscountProperty = async function (lineIndex = 1) {
  console.log('DEBUG: Manually adding group discount property to line', lineIndex);

  const cart = await fetch('/cart.js').then((r) => r.json());
  const item = cart.items[lineIndex - 1];

  if (!item) {
    console.error('Item not found at line', lineIndex);
    return;
  }

  const updateData = {
    line: lineIndex,
    quantity: item.quantity,
    properties: {
      ...item.properties,
      _allow_group_discount: 'true',
      Debug_Added: new Date().toISOString(),
    },
  };

  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData),
  });

  const result = await response.json();
  console.log('Debug update result:', result);
};

// Export functions for use in other scripts
window.cartGroupDiscountHandler = {
  updateCartItemPropertiesBackground,
  shouldAllowGroupDiscount,
  fetchPricingDataForItem,
  updateVisualPrices,
  updateCartTotals,
  debugAddGroupDiscountProperty,
  showPriceLoaders,
  hidePriceLoader,
  hideCartTotalLoaders,
  handleZeroQuantity,
};
